import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, Capabilities } from '@/api/client'
import { Select } from '@/components/Select'
import { SortableTable, type Column } from '@/components/SortableTable'
import { StatTile } from '@/components/StatTile'
import { useMe } from '@/features/auth/useAuth'
import { fmtDate, fmtNumber, useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { projectsMessages } from '@/i18n/messages/projects'

interface ProjectCable {
  cableKey: string; name: string; providerId: string | null; provider: string | null; inCatalogue: boolean
  plannedM: number | null; laidM: number; entries: number; crews: number
  firstLaid: string | null; lastLaid: string | null; implausibleEntries: number; note: string | null
}
interface CableOption { cableKey: string; name: string; providerId: string | null; provider: string | null; projectProvider: boolean }
interface ProjectMaterialData {
  projectId: string; providerId: string | null; implausibleThresholdM: number
  cables: ProjectCable[]
  byCrew: { crew: string | null; laidM: number; entries: number }[]
  implausible: { day: string | null; crew: string | null; cable: string; lengthM: number }[]
  catalogue: CableOption[]
  /** Drum figures per cable (inventory phase 3); empty until drums are sent to the project. */
  inventory: { cableName: string; onSiteM: number; unaccountedM: number; status: 'ok' | 'missing' | 'over'; shortM: number | null }[] | null
}

const INPUT =
  'w-full rounded-lg border border-neutral-300 px-2.5 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-950'

/**
 * Cable (duct) on a project: what it declares against what its crews reported laying, per cable
 * type. Inventory phase 1 (docs/inventory-design.md). Laid metres come from the Aufmaß reports
 * (AU answers_cable); declared metres are the app's own (aufmass.project_cable_plan).
 */
export function ProjectMaterial({ projectId }: { projectId: string }) {
  const t = useT(projectsMessages)
  const { data: me } = useMe()
  const canEdit = me?.capabilities.includes(Capabilities.EditAufmass) ?? false
  const [editing, setEditing] = useState<ProjectCable | 'new' | null>(null)

  const { data, isPending, error } = useQuery<ProjectMaterialData>({
    queryKey: ['project-material', projectId],
    queryFn: () => api.get<ProjectMaterialData>(`/api/projects/${projectId}/material`),
  })

  if (isPending) return <div className="h-40 animate-pulse rounded-2xl bg-neutral-100 dark:bg-neutral-800" />
  if (error || !data) return <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{t('cableCouldNotLoad')}</p>

  const declared = data.cables.filter((c) => c.plannedM != null)
  const plannedTotal = declared.reduce((s, c) => s + (c.plannedM ?? 0), 0)
  const laidOnDeclared = declared.reduce((s, c) => s + c.laidM, 0)
  const laidTotal = data.cables.reduce((s, c) => s + c.laidM, 0)

  const inv = new Map((data.inventory ?? []).map((i) => [i.cableName.trim().replace(/\s+/g, ' ').toLowerCase(), i]))
  const invFor = (c: ProjectCable) => inv.get(c.name.trim().replace(/\s+/g, ' ').toLowerCase())

  const columns: Column<ProjectCable>[] = [
    {
      key: 'cable', header: t('cableColCable'), sortValue: (c) => c.name.toLowerCase(),
      render: (c) => (
        <div className="min-w-[14rem]">
          <div className="font-medium">{c.name}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-neutral-500">
            {c.provider && <span>{c.provider}</span>}
            {!c.inCatalogue && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
                title={t('cableNotInCatalogueHint')}>{t('cableNotInCatalogue')}</span>
            )}
            {c.note && <span className="italic">{c.note}</span>}
          </div>
        </div>
      ),
    },
    {
      key: 'planned', header: t('cableColDeclared'), align: 'right', sortValue: (c) => c.plannedM ?? -1,
      render: (c) => <span className="tabular">{c.plannedM == null ? '—' : `${fmtNumber(c.plannedM)} m`}</span>,
    },
    {
      key: 'laid', header: t('cableColLaid'), align: 'right', sortValue: (c) => c.laidM,
      render: (c) => (
        <span className="tabular" title={c.implausibleEntries ? t('cableImplausibleHint', { count: c.implausibleEntries }) : undefined}>
          {fmtNumber(c.laidM)} m{c.implausibleEntries > 0 && <span className="ml-1 text-amber-600">⚠</span>}
        </span>
      ),
    },
    {
      key: 'progress', header: t('cableColProgress'), sortValue: (c) => (c.plannedM ? c.laidM / c.plannedM : -1),
      render: (c) => <Progress planned={c.plannedM} laid={c.laidM} />,
    },
    ...(inv.size > 0 ? [
      {
        key: 'onsite', header: t('cableColOnSite'), align: 'right' as const, sortValue: (c: ProjectCable) => invFor(c)?.onSiteM ?? -1,
        render: (c: ProjectCable) => {
          const i = invFor(c)
          return i ? <span className="tabular">{fmtNumber(i.onSiteM)} m{i.shortM != null && <span className="ml-1 text-amber-600" title={`−${fmtNumber(i.shortM)} m`}>⚠</span>}</span> : <span className="text-neutral-400">—</span>
        },
      },
      {
        key: 'unacc', header: t('cableColUnaccounted'), align: 'right' as const, sortValue: (c: ProjectCable) => invFor(c)?.unaccountedM ?? 0,
        render: (c: ProjectCable) => {
          const i = invFor(c)
          if (!i) return <span className="text-neutral-400">—</span>
          const tone = i.status === 'missing' ? 'text-red-700 dark:text-red-300' : i.status === 'over' ? 'text-amber-700 dark:text-amber-300' : 'text-green-700 dark:text-green-300'
          return <span className={`tabular font-semibold ${tone}`} title={t('cableUnaccountedHint')}>{i.unaccountedM > 0 ? '+' : ''}{fmtNumber(i.unaccountedM)} m</span>
        },
      },
    ] : []),
    { key: 'crews', header: t('cableColCrews'), align: 'right', sortValue: (c) => c.crews, render: (c) => <span className="tabular">{c.crews}</span> },
    {
      key: 'last', header: t('cableColLastLaid'), sortValue: (c) => c.lastLaid ?? '',
      render: (c) => <span className="tabular whitespace-nowrap text-xs text-neutral-500">{c.lastLaid ? fmtDate(c.lastLaid) : '—'}</span>,
    },
    ...(canEdit ? [{
      key: 'edit', header: '',
      render: (c: ProjectCable) => (
        <button type="button" onClick={() => setEditing(c)}
          className="rounded-lg px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-200 dark:hover:bg-brand-500/10">
          {c.plannedM == null ? t('cableDeclare') : t('cableEdit')}
        </button>
      ),
    }] : []),
  ]

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">{t('cableTitle')}</h2>
          <p className="text-xs text-neutral-500">{t('cableSubtitle')}</p>
        </div>
        {canEdit && (
          <button type="button" onClick={() => setEditing('new')}
            className="rounded-xl bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700">
            + {t('cableDeclareNew')}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <StatTile label={t('cableTileDeclared')} value={plannedTotal} unit="m" />
        <StatTile label={t('cableTileLaidDeclared')} value={laidOnDeclared} unit="m" />
        <StatTile label={t('cableTileRemaining')} value={Math.max(plannedTotal - laidOnDeclared, 0)} unit="m" />
        <StatTile label={t('cableTileLaidAll')} value={laidTotal} unit="m" />
      </div>

      <SortableTable columns={columns} rows={data.cables} rowKey={(c) => c.cableKey}
        empty={t('cableEmpty')} />

      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
        {data.byCrew.length > 0 && (
          <div className="rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
            <h3 className="text-sm font-semibold">{t('cableByCrew')}</h3>
            <ul className="mt-2 divide-y divide-neutral-100 text-sm dark:divide-neutral-800">
              {data.byCrew.map((c, i) => (
                <li key={`${c.crew}-${i}`} className="flex justify-between gap-3 py-1.5">
                  <span className="truncate">{c.crew ?? '—'}</span>
                  <span className="tabular shrink-0 text-neutral-600 dark:text-neutral-300">{fmtNumber(c.laidM)} m</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {data.implausible.length > 0 && (
          <div className="rounded-2xl bg-amber-50 p-4 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/30">
            <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">⚠ {t('cableImplausibleTitle')}</h3>
            <p className="mt-0.5 text-xs text-amber-800 dark:text-amber-300">
              {t('cableImplausibleBody', { max: fmtNumber(data.implausibleThresholdM) })}
            </p>
            <ul className="mt-2 space-y-1 text-sm">
              {data.implausible.map((x, i) => (
                <li key={i} className="flex justify-between gap-3">
                  <span className="min-w-0 truncate">{fmtDate(x.day)} · {x.crew ?? '—'} · {x.cable}</span>
                  <span className="tabular shrink-0 font-semibold">{fmtNumber(x.lengthM)} m</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {editing && (
        <CablePlanDialog projectId={projectId} data={data}
          line={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </section>
  )
}

function Progress({ planned, laid }: { planned: number | null; laid: number }) {
  if (!planned) return <span className="text-xs text-neutral-400">—</span>
  const pct = (laid / planned) * 100
  const over = pct > 100
  return (
    <div className="flex min-w-[7rem] items-center gap-2">
      <div className="h-2 flex-1 rounded-full bg-neutral-100 dark:bg-neutral-800">
        <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: over ? 'var(--viz-serious)' : 'var(--viz-good)' }} />
      </div>
      <span className={`tabular w-10 text-right text-xs ${over ? 'font-semibold text-amber-700 dark:text-amber-300' : 'text-neutral-500'}`}>
        {fmtNumber(pct, 0)}%
      </span>
    </div>
  )
}

/**
 * Declare or change one cable's metres — an explicit Edit → panel, never inline editing.
 * The list offers the catalogue (the project's own provider first) plus anything already laid
 * on the project, so a cable known only by name can be declared too.
 */
function CablePlanDialog({ projectId, data, line, onClose }:
  { projectId: string; data: ProjectMaterialData; line: ProjectCable | null; onClose: () => void }) {
  const t = useT(projectsMessages)
  const tc = useT(commonMessages)
  const qc = useQueryClient()
  const [key, setKey] = useState(line?.cableKey ?? '')
  const [planned, setPlanned] = useState(line?.plannedM != null ? String(line.plannedM) : '')
  const [note, setNote] = useState(line?.note ?? '')
  const [error, setError] = useState<string | null>(null)

  // Every cable that can be declared: laid on this project, then the catalogue.
  const options = useMemo(() => {
    const seen = new Set<string>()
    const out: { key: string; name: string; providerId: string | null; provider: string | null; group: 'laid' | 'own' | 'other' }[] = []
    for (const c of data.cables) {
      if (seen.has(c.cableKey)) continue
      seen.add(c.cableKey)
      out.push({ key: c.cableKey, name: c.name, providerId: c.providerId, provider: c.provider, group: 'laid' })
    }
    for (const c of data.catalogue) {
      if (seen.has(c.cableKey)) continue
      seen.add(c.cableKey)
      out.push({ key: c.cableKey, name: c.name, providerId: c.providerId, provider: c.provider, group: c.projectProvider ? 'own' : 'other' })
    }
    return out
  }, [data])
  const chosen = options.find((o) => o.key === key)

  const invalidate = () => qc.invalidateQueries({ queryKey: ['project-material', projectId] })
  const save = useMutation({
    mutationFn: () => api.put(`/api/projects/${projectId}/material/plan`, {
      cableKey: key, cableName: chosen!.name, providerId: chosen!.providerId,
      plannedM: Number(planned.replace(',', '.')), note: note.trim() || null,
    }),
    onSuccess: async () => { await invalidate(); onClose() },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  })
  const remove = useMutation({
    mutationFn: () => api.del(`/api/projects/${projectId}/material/plan?cableKey=${encodeURIComponent(key)}`),
    onSuccess: async () => { await invalidate(); onClose() },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  })
  const [confirmRemove, setConfirmRemove] = useState(false)

  const metres = Number(planned.replace(',', '.'))
  const valid = Boolean(chosen) && planned.trim() !== '' && Number.isFinite(metres) && metres >= 0 && metres <= 1_000_000
  const groupLabel = { laid: t('cableGroupLaid'), own: t('cableGroupOwn'), other: t('cableGroupOther') }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="flex max-h-[min(88vh,100dvh-2rem)] w-full max-w-xl flex-col rounded-2xl bg-white shadow-xl ring-1 ring-black/5 dark:bg-neutral-900 dark:ring-white/10"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 border-b border-neutral-200 p-5 dark:border-neutral-800">
          <div>
            <h2 className="text-lg font-semibold">{line?.plannedM != null ? t('cableEditTitle') : t('cableDeclareTitle')}</h2>
            <p className="mt-0.5 text-sm text-neutral-500">{t('cableDialogHint')}</p>
          </div>
          <button onClick={onClose} aria-label={tc('close')}
            className="grid size-10 shrink-0 place-items-center rounded-xl text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200">✕</button>
        </div>

        <div className="space-y-4 overflow-y-auto p-5">
          {line ? (
            <div>
              <div className="text-xs text-neutral-500">{t('cableColCable')}</div>
              <div className="font-medium">{line.name}</div>
              {line.provider && <div className="text-xs text-neutral-500">{line.provider}</div>}
            </div>
          ) : (
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">{t('cableColCable')}</span>
              <Select value={key} onChange={setKey} placeholder={t('cableSearch')}
                options={options.map((o) => ({
                  value: o.key,
                  label: o.provider ? `${o.name} — ${o.provider}` : o.name,
                  group: groupLabel[o.group],
                }))} />
            </label>
          )}

          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">{t('cableDeclaredMetres')}</span>
            <div className="flex items-center gap-2">
              <input inputMode="decimal" value={planned} onChange={(e) => setPlanned(e.target.value)} className={`${INPUT} tabular max-w-[12rem]`} autoFocus={Boolean(line)} />
              <span className="text-sm text-neutral-500">m</span>
            </div>
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">{t('cableNote')}</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} className={INPUT} />
          </label>

          {error && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{error}</p>}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 p-4 dark:border-neutral-800">
          <div>
            {line?.plannedM != null && (confirmRemove ? (
              <span className="flex items-center gap-2 text-sm">
                {t('cableRemoveConfirm')}
                <button type="button" onClick={() => remove.mutate()} disabled={remove.isPending}
                  className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">{tc('delete')}</button>
                <button type="button" onClick={() => setConfirmRemove(false)} className="text-sm text-neutral-500 hover:underline">{tc('cancel')}</button>
              </span>
            ) : (
              <button type="button" onClick={() => setConfirmRemove(true)} className="text-sm text-red-600 hover:underline">{t('cableRemove')}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-xl px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800">{tc('cancel')}</button>
            <button type="button" onClick={() => save.mutate()} disabled={!valid || save.isPending}
              className="rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">{tc('save')}</button>
          </div>
        </div>
      </div>
    </div>
  )
}
